import { supabase } from "@/integrations/supabase/client";

// Consent audit trail (public.user_consents). Every consent event, accepting
// the Terms/Privacy/Cookie policies at a given version, opting in to marketing
// by channel, or consenting to property photo / social-media use, is recorded
// here with server-captured IP + user agent (via the log-consent edge fn).
//
// Because the marketing/photo consents are often captured at the same moment a
// user authenticates (and the Google path round-trips through an OAuth redirect
// that unmounts the form), we mirror termsAcceptance: stash a pending payload
// before authenticating, then flush it once a session exists, see
// flushPendingConsents, called from useAuth on auth state changes.

export type ConsentType =
  | "terms"
  | "privacy"
  | "cookies"
  | "marketing_email"
  | "marketing_sms"
  | "marketing_whatsapp"
  | "property_photos";

export interface ConsentRecord {
  type: ConsentType;
  granted: boolean;
  /** For document consents; defaults to the type when omitted. */
  documentSlug?: string;
  /** Resolved server-side from legal_documents when omitted. */
  documentVersion?: number;
  propertyId?: string;
  source?: string;
}

interface MarketingFlags {
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
}

interface PendingConsent {
  consents: ConsentRecord[];
  marketing?: MarketingFlags;
}

const PENDING_KEY = "msth_pending_consents_v1";

/**
 * Record one or more consent events immediately (a session must exist). Routed
 * through the log-consent edge function for real IP/UA capture. Best-effort:
 * failures are logged, never thrown, so they can't block a submission.
 */
export async function logConsent(
  records: ConsentRecord | ConsentRecord[],
): Promise<void> {
  const list = Array.isArray(records) ? records : [records];
  if (list.length === 0) return;
  try {
    const { error } = await supabase.functions.invoke("log-consent", {
      body: { consents: list },
    });
    if (error) console.warn("[consent] log-consent failed", error);
  } catch (err) {
    console.warn("[consent] log-consent threw", err);
  }
}

/**
 * Stash consents (and optional marketing opt-ins) to flush after the user
 * authenticates. Merges with any already-pending payload.
 */
export function stashPendingConsent(payload: PendingConsent): void {
  if (!payload.consents.length && !payload.marketing) return;
  try {
    const existing = readPending();
    const merged: PendingConsent = {
      consents: [...(existing?.consents ?? []), ...payload.consents],
      marketing: payload.marketing ?? existing?.marketing,
    };
    localStorage.setItem(PENDING_KEY, JSON.stringify(merged));
  } catch {
    /* storage disabled, the consent copy is still shown to the user */
  }
}

function readPending(): PendingConsent | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingConsent) : null;
  } catch {
    return null;
  }
}

/**
 * Flush any consents stashed before authentication: write the marketing
 * opt-in flags to the user's profile and append the consent rows. Idempotent
 * and safe to call on every auth change, no-ops when nothing is pending. The
 * pending payload is cleared only after a successful write so a transient
 * failure retries on the next auth change.
 */
export async function flushPendingConsents(userId: string): Promise<void> {
  const pending = readPending();
  if (!pending) return;

  if (pending.marketing) {
    // Only ever turn opt-ins ON here so re-authenticating never silently
    // clears a preference the user set elsewhere. Opting out happens via
    // settings / unsubscribe / the privacy center.
    const update: Record<string, boolean> = {};
    if (pending.marketing.email) update.marketing_email_opt_in = true;
    if (pending.marketing.sms) update.marketing_sms_opt_in = true;
    if (pending.marketing.whatsapp) update.marketing_whatsapp_opt_in = true;
    if (Object.keys(update).length) {
      const { error } = await supabase
        .from("profiles")
        .update(update)
        .eq("user_id", userId);
      if (error) {
        console.warn("[consent] failed to persist marketing opt-ins", error);
        return; // retry the whole payload next auth change
      }
    }
  }

  if (pending.consents.length) await logConsent(pending.consents);

  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
